import { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/layouts/RootLayout';
import ApperIcon from '@/components/ApperIcon';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

const MATERIALS = ['Cedar', 'Vinyl', 'Chain Link', 'Aluminum', 'Other'];

const FAQ = [
  ['How do I estimate the number of fence posts?', 'Enter the total fence length and the maximum spacing you plan between posts. The calculator divides the run into sections, rounds up to a whole section, then adds one post to close the run plus any extra posts you enter for corners, ends or gate conditions.'],
  ['How does the picket count work?', 'Enter the picket width and gap in inches. The calculator divides the fence run in inches by picket width plus gap, then applies the optional waste percentage you enter.'],
  ['Does this calculate structural requirements?', 'No. It is a quantity-planning tool. Post depth, footing size, wind exposure, frost depth, engineering, local code and manufacturer requirements are outside this calculator.'],
  ['Are the spacing or waste numbers recommendations?', 'No. PicketOps does not choose those values for you. Enter the spacing, picket dimensions, rail count, waste allowance and concrete quantity that match your system and project.'],
];

const num = value => Number(value);

export default function FenceMaterialTakeoffCalculator() {
  const { isAuthenticated } = useAuth();
  const [searchParams] = useSearchParams();
  const incomingMaterial = MATERIALS.includes(searchParams.get('material')) ? searchParams.get('material') : 'Cedar';
  const incomingGates = searchParams.get('gates') || '0';
  const incomingTearout = searchParams.get('tearout') || '0';
  const [material, setMaterial] = useState(incomingMaterial);
  const [length, setLength] = useState(searchParams.get('feet') || '');
  const [postSpacing, setPostSpacing] = useState('');
  const [extraPosts, setExtraPosts] = useState('0');
  const [railsPerSection, setRailsPerSection] = useState('');
  const [picketWidth, setPicketWidth] = useState('');
  const [picketGap, setPicketGap] = useState('');
  const [waste, setWaste] = useState('0');
  const [bagsPerPost, setBagsPerPost] = useState('');
  const [copied, setCopied] = useState(false);

  const result = useMemo(() => {
    const feet = num(length);
    const spacing = num(postSpacing);
    const extras = num(extraPosts);
    const railsEach = num(railsPerSection);
    const width = num(picketWidth);
    const gap = num(picketGap);
    const wastePct = num(waste);
    const bagsEach = num(bagsPerPost);

    const railsValid = String(railsPerSection).trim() === '' || (Number.isInteger(railsEach) && railsEach >= 0);
    const picketsBlank = String(picketWidth).trim() === '' && String(picketGap).trim() === '';
    const picketsValid = picketsBlank || (
      String(picketWidth).trim() !== ''
      && String(picketGap).trim() !== ''
      && Number.isFinite(width) && width > 0
      && Number.isFinite(gap) && gap >= 0
    );
    const bagsValid = String(bagsPerPost).trim() === '' || (Number.isFinite(bagsEach) && bagsEach >= 0);
    const coreValid = String(length).trim() !== ''
      && String(postSpacing).trim() !== ''
      && Number.isFinite(feet) && feet > 0
      && Number.isFinite(spacing) && spacing > 0
      && Number.isInteger(extras) && extras >= 0
      && Number.isFinite(wastePct) && wastePct >= 0 && wastePct <= 100
      && railsValid && picketsValid && bagsValid;

    if (!coreValid) {
      return { valid:false, sections:0, posts:0, rails:null, pickets:null, bags:null };
    }

    const sections = Math.ceil(feet / spacing);
    const posts = sections + 1 + extras;
    const rails = String(railsPerSection).trim() !== '' && Number.isFinite(railsEach) && railsEach >= 0
      ? Math.ceil(sections * railsEach)
      : null;

    const picketInputsReady = !picketsBlank;
    const rawPickets = picketInputsReady ? Math.ceil((feet * 12) / (width + gap)) : null;
    const pickets = rawPickets === null ? null : Math.ceil(rawPickets * (1 + wastePct / 100));

    const bags = String(bagsPerPost).trim() !== '' && Number.isFinite(bagsEach) && bagsEach >= 0
      ? Math.ceil(posts * bagsEach)
      : null;

    return { valid:true, sections, posts, rails, pickets, bags };
  }, [length, postSpacing, extraPosts, railsPerSection, picketWidth, picketGap, waste, bagsPerPost]);

  const takeoffText = useMemo(() => {
    if (!result.valid) return '';
    return [
      'PicketOps Fence Material Takeoff',
      'Material: ' + material,
      'Fence length: ' + Number(length).toLocaleString() + ' ft',
      'Maximum post spacing entered: ' + Number(postSpacing).toLocaleString() + ' ft',
      '',
      'APPROXIMATE QUANTITIES',
      'Fence sections: ' + result.sections,
      'Posts: ' + result.posts,
      'Rails: ' + (result.rails === null ? 'Not calculated' : result.rails),
      'Pickets / boards with entered waste: ' + (result.pickets === null ? 'Not calculated' : result.pickets),
      'Concrete bags: ' + (result.bags === null ? 'Not calculated' : result.bags),
      '',
      'Extra posts entered: ' + Number(extraPosts || 0),
      'Waste allowance entered: ' + Number(waste || 0) + '%',
      '',
      'Planning estimate only. Confirm layout, corners, gate posts, product dimensions, structural requirements, manufacturer instructions and local code before ordering.',
    ].join('\n');
  }, [result, material, length, postSpacing, extraPosts, waste]);

  const copyTakeoff = async () => {
    if (!takeoffText) return;
    try {
      await navigator.clipboard.writeText(takeoffText);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1800);
    } catch {
      setCopied(false);
    }
  };

  const clear = () => {
    setMaterial('Cedar');
    setLength('');
    setPostSpacing('');
    setExtraPosts('0');
    setRailsPerSection('');
    setPicketWidth('');
    setPicketGap('');
    setWaste('0');
    setBagsPerPost('');
    setCopied(false);
  };

  const workspaceTarget = isAuthenticated ? '/estimates' : '/contractor/signup';
  const hasInput = [length,postSpacing,railsPerSection,picketWidth,picketGap,bagsPerPost].some(value=>String(value).trim()!=='')
    || extraPosts !== '0' || waste !== '0';

  return (
    <div className="picketops-public min-h-svh bg-background text-foreground">
      <header className="max-w-6xl mx-auto px-4 pt-20 pb-6 sm:pr-32">
        <Link to="/" aria-label="PicketOps home" className="inline-flex items-center gap-3 rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
          <span aria-hidden="true" className="flex size-10 items-center justify-center rounded-md bg-primary text-primary-foreground font-heading text-xl font-bold">P</span>
          <span><span className="block font-heading text-xl font-bold tracking-tight">PICKETOPS</span><span className="block text-xs text-muted-foreground">Revenue OS for fence contractors</span></span>
        </Link>
        <nav aria-label="Breadcrumb" className="mt-4 text-sm text-muted-foreground">
          <Link to="/" className="hover:underline">Home</Link><span aria-hidden="true"> / </span>
          <Link to="/fence-contractor-tools" className="hover:underline">Free tools</Link><span aria-hidden="true"> / </span>
          <span aria-current="page">Fence material takeoff calculator</span>
        </nav>
      </header>

      <main className="max-w-6xl mx-auto px-4 pb-16 space-y-10">
        <section className="picketops-revenue-surface overflow-hidden rounded-2xl border border-primary/25 bg-card shadow-(--shadow-md)">
          <div className="grid lg:grid-cols-[1.05fr_0.95fr]">
            <div className="p-6 sm:p-8 lg:p-10">
              <p className="text-xs font-semibold uppercase tracking-[0.16em] text-primary">Free material takeoff calculator · No signup required</p>
              <h1 className="mt-4 text-4xl sm:text-5xl font-heading font-bold tracking-tight leading-tight">Estimate fence posts, sections, rails and pickets from your layout</h1>
              <p className="mt-5 max-w-2xl text-base leading-relaxed text-muted-foreground">Enter your fence length and installation assumptions to create an approximate material takeoff before pricing, ordering or writing the final scope.</p>
              <div className="mt-6 flex flex-wrap gap-3">
                <Button asChild><a href="#takeoff-calculator">Build material takeoff <ApperIcon name="ArrowDown" size={16}/></a></Button>
                <Button variant="outline" asChild><Link to="/fence-estimate-calculator">Calculate selling price</Link></Button>
              </div>
            </div>
            <div className="border-t lg:border-t-0 lg:border-l border-border bg-muted/30 p-6 sm:p-8 lg:p-10">
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">What you control</p>
              <div className="mt-5 grid grid-cols-2 gap-3">
                {[
                  ['Ruler','Post spacing'],
                  ['Rows3','Rails per section'],
                  ['PanelTop','Picket width + gap'],
                  ['Percent','Waste allowance'],
                ].map(([icon,label])=><div key={label} className="rounded-xl border border-border bg-background p-4"><ApperIcon name={icon} size={18} className="text-primary"/><p className="mt-3 text-sm font-semibold">{label}</p></div>)}
              </div>
              <p className="mt-5 text-xs leading-relaxed text-muted-foreground">PicketOps does not select spacing, structural details, waste or concrete requirements for you.</p>
            </div>
          </div>
        </section>

        <section id="takeoff-calculator" aria-labelledby="takeoff-heading" className="grid gap-5 scroll-mt-6 lg:grid-cols-[0.9fr_1.1fr]">
          <Card>
            <CardContent className="p-5 sm:p-6 space-y-5">
              <div><p className="text-xs font-semibold uppercase tracking-wide text-primary">Takeoff inputs</p><h2 id="takeoff-heading" className="mt-2 text-2xl font-heading font-semibold">Enter the layout assumptions you actually plan to use.</h2></div>

              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-2"><Label htmlFor="takeoff-material">Fence material</Label><Select value={material} onValueChange={setMaterial}><SelectTrigger id="takeoff-material"><SelectValue/></SelectTrigger><SelectContent>{MATERIALS.map(value=><SelectItem key={value} value={value}>{value}</SelectItem>)}</SelectContent></Select></div>
                <div className="space-y-2"><Label htmlFor="takeoff-length">Fence length (ft)</Label><Input id="takeoff-length" type="number" min="0" step="0.01" inputMode="decimal" value={length} onChange={event=>setLength(event.target.value)} placeholder="180"/></div>
                <div className="space-y-2"><Label htmlFor="takeoff-spacing">Maximum post spacing (ft)</Label><Input id="takeoff-spacing" type="number" min="0.01" step="0.01" inputMode="decimal" value={postSpacing} onChange={event=>setPostSpacing(event.target.value)} placeholder="8"/></div>
                <div className="space-y-2"><Label htmlFor="takeoff-extra-posts">Extra posts for corners / gates</Label><Input id="takeoff-extra-posts" type="number" min="0" step="1" inputMode="numeric" value={extraPosts} onChange={event=>setExtraPosts(event.target.value)} placeholder="0"/></div>
                <div className="space-y-2"><Label htmlFor="takeoff-rails">Rails per section <span className="text-muted-foreground">(optional)</span></Label><Input id="takeoff-rails" type="number" min="0" step="1" inputMode="numeric" value={railsPerSection} onChange={event=>setRailsPerSection(event.target.value)} placeholder="3"/></div>
                <div className="space-y-2"><Label htmlFor="takeoff-bags">Concrete bags per post <span className="text-muted-foreground">(optional)</span></Label><Input id="takeoff-bags" type="number" min="0" step="0.01" inputMode="decimal" value={bagsPerPost} onChange={event=>setBagsPerPost(event.target.value)} placeholder="2"/></div>
              </div>

              <div className="rounded-xl border border-border bg-muted/20 p-4 space-y-4">
                <div><p className="text-sm font-semibold">Picket / board estimate <span className="font-normal text-muted-foreground">(optional)</span></p><p className="mt-1 text-xs text-muted-foreground">Enter actual board width and desired gap. Leave blank for panel, chain-link or other systems where this count is not useful.</p></div>
                <div className="grid sm:grid-cols-3 gap-4">
                  <div className="space-y-2"><Label htmlFor="takeoff-width">Picket width (in)</Label><Input id="takeoff-width" type="number" min="0.01" step="0.01" inputMode="decimal" value={picketWidth} onChange={event=>setPicketWidth(event.target.value)} placeholder="5.5"/></div>
                  <div className="space-y-2"><Label htmlFor="takeoff-gap">Gap (in)</Label><Input id="takeoff-gap" type="number" min="0" step="0.01" inputMode="decimal" value={picketGap} onChange={event=>setPicketGap(event.target.value)} placeholder="0.25"/></div>
                  <div className="space-y-2"><Label htmlFor="takeoff-waste">Waste allowance (%)</Label><Input id="takeoff-waste" type="number" min="0" max="100" step="1" inputMode="decimal" value={waste} onChange={event=>setWaste(event.target.value)} placeholder="0"/></div>
                </div>
              </div>

              {hasInput && !result.valid && <p role="alert" className="rounded-lg border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive">Enter positive fence length and post spacing. Extra posts and rails/section must be whole nonnegative numbers. Enter both picket width and gap together, keep optional concrete bags nonnegative, and use waste from 0 to 100%.</p>}
              <Button type="button" variant="ghost" onClick={clear}><ApperIcon name="RotateCcw" size={16}/>Clear calculator</Button>
            </CardContent>
          </Card>

          <div className="space-y-5">
            <section className="overflow-hidden rounded-2xl border border-primary/25 bg-card shadow-(--shadow-sm)">
              <div className="border-b border-border bg-primary/5 p-5 sm:p-6"><p className="text-xs font-semibold uppercase tracking-wide text-primary">Approximate material takeoff</p><h2 className="mt-2 text-2xl font-heading font-semibold">{result.valid?'Quantities ready to review':'Complete fence length and post spacing'}</h2></div>
              <dl className="grid grid-cols-2 gap-px bg-border sm:grid-cols-3">
                {[
                  ['Fence sections',result.valid?result.sections:'—','Between calculated line posts'],
                  ['Posts',result.valid?result.posts:'—','Includes entered extra posts'],
                  ['Rails',result.valid&&result.rails!==null?result.rails:'—','Only if rails/section entered'],
                  ['Pickets / boards',result.valid&&result.pickets!==null?result.pickets:'—','Includes entered waste'],
                  ['Concrete bags',result.valid&&result.bags!==null?result.bags:'—','Only if bags/post entered'],
                  ['Fence length',result.valid?Number(length).toLocaleString()+' ft':'—','Input used for this takeoff'],
                ].map(([label,value,detail])=><div key={label} className="bg-card p-4 sm:p-5"><dt className="text-xs text-muted-foreground">{label}</dt><dd className="mt-1 text-2xl font-bold tabular-nums">{value}</dd><p className="mt-1 text-xs text-muted-foreground">{detail}</p></div>)}
              </dl>
              <div className="border-t border-border p-5 sm:p-6"><p className="text-xs leading-relaxed text-muted-foreground">Planning quantities only. Real orders depend on layout breaks, corners, gate framing, product dimensions, grade changes, cut waste, stock lengths, structural requirements and local conditions.</p></div>
            </section>

            <div className="flex flex-wrap gap-3">
              <Button type="button" onClick={copyTakeoff} disabled={!result.valid}><ApperIcon name={copied?'Check':'Copy'} size={16}/>{copied?'Copied':'Copy takeoff'}</Button>
              {result.valid?<Button variant="outline" asChild><a href={'data:text/plain;charset=utf-8,'+encodeURIComponent(takeoffText)} download="picketops-fence-material-takeoff.txt"><ApperIcon name="Download" size={16}/>Download takeoff</a></Button>:<Button variant="outline" disabled><ApperIcon name="Download" size={16}/>Download takeoff</Button>}
              {result.valid&&<Button variant="outline" asChild><Link to={'/fence-labor-crew-planner?feet='+encodeURIComponent(length)+'&job='+encodeURIComponent('Fence material takeoff')}><ApperIcon name="Users" size={16}/>Plan crew labor</Link></Button>}
              {result.valid&&<Button variant="outline" asChild><Link to={'/fence-estimate-calculator?feet='+encodeURIComponent(length)+'&gates='+encodeURIComponent(incomingGates)+'&tearout='+encodeURIComponent(incomingTearout)}><ApperIcon name="Calculator" size={16}/>Price this footage</Link></Button>}
              {result.valid&&<Button variant="outline" asChild><Link to={'/fence-estimate-template?feet='+encodeURIComponent(length)+'&material='+encodeURIComponent(material)+'&gates='+encodeURIComponent(incomingGates)+'&tearout='+encodeURIComponent(incomingTearout)}><ApperIcon name="FilePenLine" size={16}/>Build the scope</Link></Button>}
              <Button variant="outline" asChild><Link to={workspaceTarget}>{isAuthenticated?'Open saved estimates':'Create account'} <ApperIcon name="ArrowRight" size={16}/></Link></Button>
            </div>
          </div>
        </section>

        <section aria-labelledby="takeoff-faq-heading" className="max-w-4xl mx-auto">
          <h2 id="takeoff-faq-heading" className="text-2xl font-heading font-semibold">Fence material takeoff questions</h2>
          <div className="mt-5 divide-y rounded-xl border border-border bg-card px-5">{FAQ.map(([question,answer])=><details key={question} className="py-4"><summary className="cursor-pointer rounded-md font-semibold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">{question}</summary><p className="mt-3 text-sm leading-relaxed text-muted-foreground">{answer}</p></details>)}</div>
        </section>
      </main>
    </div>
  );
}

export const route={path:'/fence-material-takeoff-calculator',layout:'public'};
